import { Button } from 'design-system/ui/atoms/button'
import { Input } from 'design-system/ui/atoms/input'
import { SendHorizontalIcon } from 'lucide-react'
import { z } from 'zod'

import { ActionSelector } from '~/ui/screens/appScreen/modules/createIntegration/actionsSelector'

interface CreateStepProps {
  serverName: string
  setServerName: (name: string) => void
  connectedId: string
  entityId: string
  setEntityId: (id: string) => void
  limitedActions: string[]
  setLimitedActions: (actions: string[]) => void
  handleSubmit: () => void
  isLoading: boolean
  appName: string
}

// Zod schema for server name validation
const serverNameSchema = z
  .string()
  .min(4, 'Server name must be at least 4 characters long')
  .max(30, 'Server name must be 30 characters or less')
  .regex(
    /^[a-zA-Z0-9- ]+$/,
    'Server name can only contain letters, numbers, spaces, and hyphens',
  )

export const CreateStep = ({
  serverName,
  setServerName,
  connectedId,
  entityId,
  setEntityId,
  limitedActions,
  setLimitedActions,
  handleSubmit,
  isLoading,
  appName,
}: CreateStepProps) => {
  const serverNameValidation = serverNameSchema.safeParse(serverName)
  const isFormValid = serverNameValidation.success && serverName.length > 0

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gray-50/50">
      <div className="mx-auto max-w-4xl px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            Configure Your MCP Server
          </h1>
          <p className="mt-2 text-gray-600">
            Set up your MCP server with the required configuration details.
          </p>
        </div>

        {/* Form Section */}
        <div className="space-y-8">
          {/* Server Configuration Card */}
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
            <h2 className="mb-6 text-lg font-medium text-gray-900">
              Server Configuration
            </h2>

            <div className="space-y-6">
              {/* Server Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Server Name *
                </label>
                <p className="mt-1 text-sm text-gray-500">
                  Choose a unique, descriptive name for your MCP server.
                </p>
                <div className="mt-3">
                  <Input
                    placeholder="e.g., github-personal, slack-workspace"
                    value={serverName}
                    className={`h-10 w-full transition-colors ${
                      serverName && !serverNameValidation.success
                        ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                    }`}
                    onChange={(e) => setServerName(e.target.value)}
                  />
                  {serverName && !serverNameValidation.success && (
                    <p className="mt-2 text-sm text-red-600">
                      {serverNameValidation.error.errors[0]?.message}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    4-30 characters • Letters, numbers, spaces, and hyphens only
                  </p>
                </div>
              </div>

              {/* Connected Account ID */}
              {connectedId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Connected Account ID
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    Auto-generated identifier for your authenticated connection.
                  </p>
                  <div className="mt-3">
                    <Input
                      value={connectedId}
                      className="h-10 w-full bg-gray-50 text-gray-600 focus:ring-1 focus:ring-gray-400"
                      disabled
                    />
                  </div>
                </div>
              )}

              {/* User ID */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700">
                  User ID
                </label>
                <p className="mt-1 text-sm text-gray-500">
                  Specify the user this server will operate on.
                </p>
                <div className="mt-3">
                  <Input
                    value={entityId}
                    onChange={(e) => setEntityId(e.target.value)}
                    placeholder={connectedId ? '' : 'e.g., user-123, default'}
                    className={`h-10 w-full ${
                      connectedId
                        ? 'bg-gray-50 text-gray-600 focus:ring-1 focus:ring-gray-400'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                    }`}
                    disabled={!!connectedId}
                  />
                </div>
              </div> */}
            </div>
          </div>

          {/* Actions Configuration Card */}
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
            <h2 className="mb-6 text-lg font-medium text-gray-900">
              Action Permissions
            </h2>

            <div>
              <ActionSelector
                appName={appName}
                limitedActions={limitedActions}
                setLimitedActions={setLimitedActions}
                isMcp={true}
              />
              <p className="mt-3 text-sm text-gray-500">
                Important actions will be preselected. Choose specific actions
                or leave empty to allow all. Limiting actions improves security
                through the principle of least privilege.
              </p>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={isLoading || !isFormValid}
              variant={isLoading || !isFormValid ? 'disabled' : 'primary'}
              size="36"
              className="min-w-[180px] justify-center"
            >
              {isLoading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Deploying...
                </>
              ) : (
                <>
                  Deploy MCP Server
                  <SendHorizontalIcon className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
